import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import { ConfigurationTemplateEditorPartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/impl/configurationTemplateEditorPartConfiguratorType.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useState } from "react";
import i18n from "i18next";
import { faBan, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PartConfigurationTemplate } from "src/api/generated/erp/db/types/tables/partConfigurationTemplate.ts";
import { PartConfigurationTemplateEditApi } from "src/api/generated/erp/configurator/management/configurationTemplates/partConfigurationTemplateEditApi.ts";
import { ConfiguratorInput } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";

export interface PartConfigurationTemplateEditorProps {
	partConfigurationTemplate: PartConfigurationTemplate;
	closeDialog: () => Promise<unknown>;
	refreshTemplates: () => Promise<unknown>;
}

export const PartConfigurationTemplateEditor = ({
	partConfigurationTemplate,
	closeDialog,
	refreshTemplates,
}: PartConfigurationTemplateEditorProps) => {
	const { palette } = useTheme();
	const [disabledComponentIds, setDisabledComponentIds] = useState<number[]>(
		partConfigurationTemplate.restrictions.disabledComponentIds,
	);

	const configuratorType: ConfigurationTemplateEditorPartConfiguratorType = {
		type: "configurationTemplateEditor",
		partConfigurationTemplateId: partConfigurationTemplate.partConfigurationTemplateId,
	};

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			readonly={false}
			configuratorComponentSx={componentSx}
			configuratorComponentContextMenu={(component) => componentContextMenu(component)}
			addSubConfiguratorButtonSx={componentSx}
			addSubConfiguratorButtonContextMenu={({ component, closeDialog }) =>
				componentContextMenu(component, closeDialog)
			}
			allowInputErrors
			onSubmit={async (result) => {
				if (result.type === "success") {
					await onSubmit(result.value);
				} else {
					await closeDialog();
				}
			}}
		/>
	);

	function componentSx(component: { configurationComponentId: number }) {
		const isDisabled = componentIsDisabled(component);
		if (isDisabled)
			return {
				backgroundColor: alpha(palette.secondary.main, 0.6),
				opacity: 0.7,
			};
		return {};
	}

	function componentContextMenu(component: { configurationComponentId: number }, afterEdit?: () => void) {
		const isDisabled = componentIsDisabled(component);
		return [
			!isDisabled && (
				<AsyncMenuButton
					key={"disableComponent"}
					label={i18n.t("disable_editing")}
					icon={faBan}
					onClick={async () => {
						setDisabledComponentIds((prev) => [...prev, component.configurationComponentId]);
						afterEdit?.();
					}}
				/>
			),
			isDisabled && (
				<AsyncMenuButton
					key={"enableComponent"}
					label={i18n.t("allow_editing")}
					icon={faCheck}
					onClick={async () => {
						setDisabledComponentIds((prev) =>
							prev.filter((id) => id !== component.configurationComponentId),
						);
						afterEdit?.();
					}}
				/>
			),
		];
	}

	function componentIsDisabled(component: { configurationComponentId: number }) {
		return disabledComponentIds.includes(component.configurationComponentId);
	}

	async function onSubmit(configuratorInput: ConfiguratorInput) {
		await PartConfigurationTemplateEditApi.update({
			partConfigurationTemplateId: partConfigurationTemplate.partConfigurationTemplateId,
			configuratorInput: configuratorInput,
			restrictions: {
				disabledComponentIds: disabledComponentIds,
			},
		});
		await refreshTemplates();
		await closeDialog();
	}
};
